// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import type { Database } from './types';

const SUPABASE_URL = "https://wqagrmomwzikqpxwiskl.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndxYWdybW9td3ppa3FweHdpc2tsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzNzMwODcsImV4cCI6MjA3MTk0OTA4N30.V059yyB0Lv4h1CyrFV_IW6tdRtUhuLa3cCG2qR-qUxw";

const isNative = Capacitor.isNativePlatform();

/**
 * Synchronous storage wrapper over Capacitor Preferences.
 * - Précharge les clés Supabase/PKCE dans un cache mémoire pour répondre
 *   de manière synchrone à Supabase (qui attend une API sync).
 */
class SyncStorage {
  private cache: Map<string, string> = new Map();
  private initialized = false;

  async initialize() {
    if (this.initialized) return;

    console.log('🔄 [SyncStorage] Initializing cache...');
    try {
      const keysResult = await (Preferences as any).keys?.();
      const allKeys: string[] = Array.isArray(keysResult?.keys) ? keysResult.keys : [];

      // Clés intéressantes (connues) + découverte
      const fallbackKeys = [
        'supabase-auth-token',
        'sb-wqagrmomwzikqpxwiskl-auth-token',
        'supabase.auth.token',
        'pkce_code_verifier',
        'supabase.auth.pkce.code_verifier',
      ];

      const interesting = new Set<string>([
        ...fallbackKeys,
        ...allKeys.filter((k) =>
          k.startsWith('sb-') ||
          k.startsWith('supabase') ||
          k.includes('auth') ||
          k.includes('pkce') ||
          k.includes('code_verifier')
        ),
      ]);

      for (const key of interesting) {
        const { value } = await Preferences.get({ key });
        if (value) {
          this.cache.set(key, value);
          console.log(`✅ [SyncStorage] Cached key: ${key}`);
        }
      }
    } catch (e) {
      console.warn('⚠️ [SyncStorage] Could not enumerate keys, using fallbacks only:', e);
      const fallbackKeys = [
        'supabase-auth-token',
        'sb-wqagrmomwzikqpxwiskl-auth-token',
        'supabase.auth.token',
        'pkce_code_verifier',
        'supabase.auth.pkce.code_verifier',
      ];
      for (const key of fallbackKeys) {
        const { value } = await Preferences.get({ key });
        if (value) {
          this.cache.set(key, value);
          console.log(`✅ [SyncStorage] Cached key: ${key}`);
        }
      }
    }

    this.initialized = true;
    console.log('✅ [SyncStorage] Cache initialized with', this.cache.size, 'keys');
  }

  getItem(key: string): string | null {
    const value = this.cache.get(key) ?? null;
    console.log(`📖 [SyncStorage] getItem(${key}):`, value ? 'FOUND' : 'NOT FOUND');
    return value;
  }

  setItem(key: string, value: string): void {
    console.log(`💾 [SyncStorage] setItem(${key})`);
    this.cache.set(key, value);
    // Persist async en arrière-plan
    Preferences.set({ key, value }).catch(err => {
      console.error(`❌ [SyncStorage] Failed to persist ${key}:`, err);
    });
  }

  removeItem(key: string): void {
    console.log(`🗑️ [SyncStorage] removeItem(${key})`);
    this.cache.delete(key);
    Preferences.remove({ key }).catch(err => {
      console.error(`❌ [SyncStorage] Failed to remove ${key}:`, err);
    });
  }
}

const syncStorage = new SyncStorage();

// Initialize and export a readiness promise for native platforms
export const storageReady = isNative
  ? syncStorage.initialize().then(() => {
      console.log('✅ SyncStorage ready for Supabase');
      return true;
    })
  : Promise.resolve(true);

/**
 * Client Supabase en PKCE :
 * - flowType: 'pkce' (indispensable sur mobile natif)
 * - detectSessionInUrl: true sur web, false sur natif (on gère via deep link + exchangeCodeForSession)
 * - storage: localStorage (web) / SyncStorage (natif)
 */
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: isNative ? (syncStorage as any) : localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: !isNative,
    flowType: 'pkce',
  },
});
